import React, { useState, useEffect } from 'react';
import { 
  Package, 
  DollarSign, 
  AlertTriangle, 
  FileText, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { dashboardAPI, invoicesAPI, materialsAPI } from '../lib/api';
import { formatCurrency, formatDate, formatStock } from '../lib/utils';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [lowStockMaterials, setLowStockMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, invoicesRes, materialsRes] = await Promise.all([
        dashboardAPI.getStats(),
        invoicesAPI.getAll(),
        materialsAPI.getAll(),
      ]);
      
      setStats(statsRes.data);
      setRecentInvoices(invoicesRes.data.slice(0, 5));
      setLowStockMaterials(
        materialsRes.data.filter(m => m.current_stock <= m.min_stock).slice(0, 5)
      );
    } catch (error) {
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Resumen general del sistema</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchData}
          className="self-start"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200" data-testid="kpi-inventory-value">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Valor Inventario</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(stats?.total_inventory_value || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#931A25]/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#931A25]" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-sm">
              <span className="text-slate-500">
                {stats?.total_raw_materials || 0} materias primas
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-daily-sales">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Ventas del Día</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(stats?.daily_sales || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-sm">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600 font-medium">{stats?.daily_invoices || 0}</span>
              <span className="text-slate-500 ml-1">facturas hoy</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-monthly-sales">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Ventas del Mes</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(stats?.monthly_sales || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-sm text-slate-500">
              Acumulado mensual
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-alerts">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Alertas Stock</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {stats?.low_stock_alerts || 0}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                stats?.low_stock_alerts > 0 ? 'bg-red-100' : 'bg-green-100'
              }`}>
                <AlertTriangle className={`h-6 w-6 ${
                  stats?.low_stock_alerts > 0 ? 'text-red-600' : 'text-green-600'
                }`} />
              </div>
            </div>
            <div className="flex items-center mt-4 text-sm">
              {stats?.low_stock_alerts > 0 ? (
                <span className="text-red-600">Requiere atención</span>
              ) : (
                <span className="text-green-600">Todo en orden</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPI */}
      <Card className="border-slate-200" data-testid="kpi-pending-payments">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Pagos Pendientes</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {formatCurrency(stats?.pending_payments || 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <Card className="border-slate-200" data-testid="recent-invoices-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold">Facturas Recientes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recentInvoices.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No hay facturas recientes
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentInvoices.map((invoice) => (
                  <div key={invoice.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-slate-500">{invoice.client_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        invoice.payment_status === 'pagado' ? 'bg-green-100 text-green-800' :
                        invoice.payment_status === 'credito' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.payment_status === 'pagado' ? 'Pagado' :
                         invoice.payment_status === 'credito' ? 'Crédito' : 'Pendiente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card className="border-slate-200" data-testid="low-stock-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold">Alertas de Stock Bajo</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {lowStockMaterials.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No hay alertas de stock bajo
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {lowStockMaterials.map((material) => (
                  <div key={material.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{material.name}</p>
                        <p className="text-sm text-slate-500">
                          Mínimo: {material.min_stock} {material.unit}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">
                        {formatStock(material.current_stock, material.unit)}
                      </p>
                      <p className="text-xs text-slate-500">Stock actual</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
